import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../../config/api';
import { APP_VERSION_LABEL } from '../../../config/version';

// Plain-language labels + badge colors for the overall freshness level
// returned by GET /api/system/status ("green" | "yellow" | "red" | "unknown").
// Lag semantics (backend api_system.py): 0 processable days missing = green;
// exactly 1 (yesterday's data still landing) = yellow "Updating" with a live
// pipeline ETA; 2+ = red.
const LEVEL_LABELS = {
  green: 'Up to date',
  yellow: 'Updating',
  red: 'Systems under maintenance',
  unknown: 'Unknown',
};

const LEVEL_CLASSES = {
  green: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  yellow: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  red: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  unknown: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
};

const formatClock = (totalSeconds) => {
  const s = Math.max(0, Math.round(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
};

/**
 * Seconds until the daily pipeline is expected to finish, from the backend's
 * ETA inputs: anchor (actual start if running, else next expected start) +
 * trailing-7-day average runtime - now. Recomputed each tick so it counts
 * down like a clock; clamped at 0.
 */
const etaRemainingSeconds = (eta, now) => {
  if (!eta || !eta.avg_seconds) return null;
  const anchor = eta.started_at || eta.expected_start_at;
  if (!anchor) return null;
  const end = new Date(anchor).getTime() + eta.avg_seconds * 1000;
  return Math.max(0, (end - now) / 1000);
};

/**
 * System page: data freshness (per-table lag against the Athena lake) and
 * build/version info for the running frontend + backend. Two real incidents
 * came from a stale deploy being mistaken for a code bug - this page is the
 * first place in the product where either is visible.
 */
const InfoPage = () => {
  const { darkMode } = useTheme();
  const { fetchWithAuth } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastChecked, setLastChecked] = useState(null);
  // Ticks every second while the "Updating" ETA countdown is showing.
  const [now, setNow] = useState(() => Date.now());

  const muted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const card = darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const rowBorder = darkMode ? 'border-gray-700' : 'border-gray-200';

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.system.status);
      if (!res.ok) throw new Error(`system status ${res.status}`);
      const data = await res.json();
      setStatus(data);
      setLastChecked(new Date());
    } catch (e) {
      setError(e.message || 'Failed to load system status');
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    load();
  }, [load]);

  const backend = status?.backend;
  const overall = status?.data?.overall;
  const tables = status?.data?.tables || [];

  const remaining = overall?.level === 'yellow' ? etaRemainingSeconds(overall.eta, now) : null;
  useEffect(() => {
    if (overall?.level !== 'yellow' || !overall?.eta) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [overall]);

  // Badge text: yellow carries the live pipeline ETA (average remaining run
  // time +- trailing-7-day standard deviation), counting down each second.
  let badgeLabel = LEVEL_LABELS[overall?.level] || LEVEL_LABELS.unknown;
  if (overall?.level === 'yellow') {
    badgeLabel = remaining != null
      ? `Updating - ETA: ${formatClock(remaining)} ± ${Math.max(1, Math.round((overall.eta.std_seconds || 0) / 60))}m`
      : 'Updating';
  }

  return (
    <div className={`p-6 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
      <h3 className="text-2xl font-bold mb-1">System</h3>
      <p className={`mb-6 ${muted}`}>Data freshness and build information.</p>

      <div className={`mb-6 p-5 rounded-xl border ${card} max-w-2xl`}>
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-semibold">Data freshness</h4>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className={`flex items-center gap-2 text-sm ${muted} hover:underline disabled:opacity-50`}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {error && (
          <div className="mb-3 px-3 py-2 rounded bg-red-100 text-red-800 text-sm">
            {error}
          </div>
        )}

        {loading && !status ? (
          <p className={muted}>Loading...</p>
        ) : !overall ? null : (
          <>
            <div className="flex items-center gap-3 mb-4">
              <span
                data-testid="freshness-badge"
                className={`inline-block text-xs font-medium px-2.5 py-1 rounded-full ${
                  LEVEL_CLASSES[overall.level] || LEVEL_CLASSES.unknown
                }`}
              >
                {badgeLabel}
              </span>
              {overall.latest_date && (
                <span className={`text-sm ${muted}`}>as of {overall.latest_date}</span>
              )}
            </div>

            {tables.length > 0 ? (
              <table className="w-full text-sm mb-3">
                <thead>
                  <tr className={muted}>
                    <th className="text-left font-medium pb-2">Table</th>
                    <th className="text-left font-medium pb-2">Latest date</th>
                    <th className="text-right font-medium pb-2">Lag (days)</th>
                  </tr>
                </thead>
                <tbody>
                  {tables.map((t) => (
                    <tr key={t.name} className={`border-t ${rowBorder}`}>
                      <td className="py-1.5 capitalize">{t.name}</td>
                      <td className="py-1.5">{t.latest_date || '-'}</td>
                      <td className="py-1.5 text-right font-mono">
                        {t.lag_days ?? '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className={`text-sm ${muted} mb-3`}>
                Freshness data is temporarily unavailable.
              </p>
            )}

            {lastChecked && (
              <p className={`text-xs ${muted}`}>
                Last checked {lastChecked.toLocaleTimeString()}
              </p>
            )}
          </>
        )}
      </div>

      <div className={`p-5 rounded-xl border ${card} max-w-2xl`}>
        <h4 className="text-lg font-semibold mb-4">Versions</h4>
        <dl className="text-sm space-y-2">
          <div className="flex justify-between">
            <dt className={muted}>Version</dt>
            <dd className="font-semibold">{APP_VERSION_LABEL}</dd>
          </div>
          <div className="flex justify-between">
            <dt className={muted}>Frontend build</dt>
            <dd className="font-mono">{process.env.REACT_APP_GIT_SHA || 'dev'}</dd>
          </div>
          <div className="flex justify-between">
            <dt className={muted}>Backend build</dt>
            <dd className="font-mono">{backend ? backend.build : '-'}</dd>
          </div>
          <div className="flex justify-between">
            <dt className={muted}>Backend started</dt>
            <dd className="font-mono">
              {backend ? new Date(backend.started_at).toLocaleString() : '-'}
            </dd>
          </div>
        </dl>
      </div>
    </div>
  );
};

export default InfoPage;
